import React from "react";
import { MarkdownCodeBlock } from "./MarkdownCodeBlock";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { cn } from "@src/lib/utils/cn";

// A minimal view of the hast nodes react-markdown hands its components.
type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  children?: HastNode[];
};

const hastText = (node: HastNode): string =>
  node.type === "text"
    ? node.value ?? ""
    : (node.children ?? []).map(hastText).join("");

const hastChildren = (node: HastNode | undefined, tagName: string) =>
  (node?.children ?? []).filter(
    (child) => child.type === "element" && child.tagName === tagName,
  );

// Each body cell's column header, for the stacked mobile layout. Set by the
// table renderer (which sees the whole table) before its cells render.
const cellLabels = new WeakMap<HastNode, { label: string; first: boolean }>();

const labelTableCells = (table: HastNode | undefined) => {
  const [head] = hastChildren(table, "thead");
  const [headRow] = hastChildren(head, "tr");
  const labels = hastChildren(headRow, "th").map((th) => hastText(th).trim());
  for (const body of hastChildren(table, "tbody"))
    for (const row of hastChildren(body, "tr"))
      hastChildren(row, "td").forEach((cell, index) =>
        cellLabels.set(cell, {
          label: labels[index] ?? "",
          first: index === 0,
        }),
      );
};

// Vertical rhythm between markdown blocks. "comfortable" is the legacy Ask
// Opus chat's spacing (unchanged); "dense" is roughly half of it for the Opus
// Agent drawer, where a long answer should read as one compact block — no
// double gap around lists (list-item margin folded into the list's space-y).
type BlockSpacing = Record<
  | "p"
  | "h1"
  | "h2"
  | "h3"
  | "list"
  | "li"
  | "pre"
  | "blockquote"
  | "hr"
  | "table",
  string
>;

const BLOCK_SPACING: Record<"comfortable" | "dense", BlockSpacing> = {
  comfortable: {
    p: "mb-4 last:mb-0",
    h1: "mt-6 mb-4",
    h2: "mt-5 mb-3",
    h3: "mt-4 mb-2",
    list: "mb-4 space-y-1",
    li: "mb-1",
    pre: "mb-4",
    blockquote: "my-4",
    hr: "my-6",
    table: "mb-4",
  },
  dense: {
    p: "mb-2 last:mb-0",
    h1: "mt-3 mb-2",
    h2: "mt-2.5 mb-1.5",
    h3: "mt-2 mb-1",
    list: "mb-2 space-y-0.5",
    li: "mb-0",
    pre: "mb-2",
    blockquote: "my-2",
    hr: "my-3",
    table: "mb-2",
  },
};

export interface RenderMarkdownOptions {
  // Tighter block rhythm for the Opus Agent answer surface only.
  dense?: boolean;
}

/**
 * Renders text as markdown without inline citations.
 * Citations are now displayed at the bottom of messages via MessageAnnotations component.
 */
export const renderMarkdown = (
  content: string,
  { dense = false }: RenderMarkdownOptions = {},
): React.ReactNode => {
  const spacing = BLOCK_SPACING[dense ? "dense" : "comfortable"];
  // Simplified to just render markdown without inline citations
  // Citations are now shown at the bottom via MessageAnnotations component
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex]}
      className="prose prose-sm max-w-none"
      components={{
        p: ({ children }) => <p className={spacing.p}>{children}</p>,
        h1: ({ children }) => (
          <h1
            className={cn(spacing.h1, "text-xl-v4 font-semibold-v4 first:mt-0")}
          >
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2
            className={cn(spacing.h2, "text-l-v4 font-semibold-v4 first:mt-0")}
          >
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3
            className={cn(spacing.h3, "text-m-v4 font-semibold-v4 first:mt-0")}
          >
            {children}
          </h3>
        ),
        ul: ({ children }) => (
          <ul className={cn(spacing.list, "list-disc pl-6")}>{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className={cn(spacing.list, "list-decimal pl-6")}>{children}</ol>
        ),
        li: ({ children }) => <li className={spacing.li}>{children}</li>,
        strong: ({ children }) => (
          <strong className="font-semibold-v4">{children}</strong>
        ),
        em: ({ children }) => <em className="italic">{children}</em>,
        code: ({ children }) => (
          <code className="rounded bg-bg-secondary px-1.5 py-0.5 font-family-mono text-code-sm">
            {children}
          </code>
        ),
        pre: ({ children }) => (
          <MarkdownCodeBlock className={spacing.pre}>
            {children}
          </MarkdownCodeBlock>
        ),
        blockquote: ({ children }) => (
          <blockquote
            className={cn(
              spacing.blockquote,
              "border-l-4 border-border-secondary pl-4 italic",
            )}
          >
            {children}
          </blockquote>
        ),
        hr: () => <hr className={cn(spacing.hr, "border-border-primary")} />,
        // Tables follow the room they actually get (a container query, so
        // a 326px chat panel on desktop counts as narrow too). With room they
        // fill the width and wrap inside cells (never narrower than 7rem, so
        // no column squeezes into a letter stack); sideways scroll is only a
        // last resort for very many columns.
        // Narrow, each row becomes a card: its first cell is the title and
        // every other cell reads "Header: value", so no column is ever
        // clipped off the side of a chat bubble.
        table: ({ children, node }) => {
          labelTableCells(node as HastNode | undefined);
          return (
            <div
              className={cn(
                spacing.table,
                "@container/md-table overflow-x-auto",
              )}
            >
              <table className="w-full border-collapse text-s-v4 @max-[560px]/md-table:block">
                {children}
              </table>
            </div>
          );
        },
        thead: ({ children }) => (
          <thead className="@max-[560px]/md-table:sr-only">{children}</thead>
        ),
        tbody: ({ children }) => (
          <tbody className="@max-[560px]/md-table:flex @max-[560px]/md-table:flex-col @max-[560px]/md-table:gap-1.5">
            {children}
          </tbody>
        ),
        tr: ({ children }) => (
          <tr className="@max-[560px]/md-table:flex @max-[560px]/md-table:flex-col @max-[560px]/md-table:gap-0.5 @max-[560px]/md-table:rounded-lg @max-[560px]/md-table:border @max-[560px]/md-table:border-border-primary @max-[560px]/md-table:bg-bg-primary @max-[560px]/md-table:px-3 @max-[560px]/md-table:py-2">
            {children}
          </tr>
        ),
        th: ({ children }) => (
          <th className="min-w-28 border border-border-primary bg-bg-primary px-2 py-1 text-left align-bottom font-semibold-v4">
            {children}
          </th>
        ),
        td: ({ children, node }) => {
          const cell = node ? cellLabels.get(node as HastNode) : undefined;
          return (
            <td
              data-label={cell?.label || undefined}
              className={cn(
                "min-w-28 border border-border-primary px-2 py-1 align-top",
                "@max-[560px]/md-table:block @max-[560px]/md-table:min-w-0 @max-[560px]/md-table:border-0 @max-[560px]/md-table:p-0",
                cell?.first
                  ? "@max-[560px]/md-table:font-semibold-v4"
                  : cell?.label &&
                      "@max-[560px]/md-table:before:mr-1 @max-[560px]/md-table:before:text-text-secondary @max-[560px]/md-table:before:content-[attr(data-label)_':']",
              )}
            >
              {children}
            </td>
          );
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
};
